import { type FormEvent, useState, useEffect } from "react";
import type { GradeLevel } from "@markeroo/shared";
import { Modal, ModalHeader, ModalBody, ModalFooter } from "@/components/Modal";
import { Select } from "@/components/Select";
import { Tooltip } from "@/components/Tooltip";
import { fieldLabelClass, inputClass } from "@/styles/variants";
import { buttonVariants } from "@markeroo/theme";
import { ui, placeholders } from "@/strings";

const disabledInputClass = `${inputClass} disabled:bg-surface-page disabled:text-text-theme-muted disabled:cursor-not-allowed`;

const GRADE_LEVELS: GradeLevel[] = ["3", "4", "5", "6", "7", "8", "9", "10", "11", "12"];

interface ClassFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: { name: string; defaultElaGradeLevel: GradeLevel }) => void;
  isSubmitting: boolean;
  initialName?: string;
  initialDefaultElaGradeLevel?: GradeLevel;
  isGCSynced?: boolean;
}

export function ClassFormModal({
  isOpen,
  onClose,
  onSubmit,
  isSubmitting,
  initialName,
  initialDefaultElaGradeLevel,
  isGCSynced = false,
}: ClassFormModalProps) {
  const [name, setName] = useState("");
  const [defaultElaGradeLevel, setDefaultElaGradeLevel] = useState<GradeLevel | "">("");

  const isEditing = !!initialName;

  useEffect(() => {
    if (isOpen) {
      setName(initialName || "");
      // For new classes, default to grade 3. For editing, use existing value.
      setDefaultElaGradeLevel(initialDefaultElaGradeLevel || (isEditing ? "" : "3"));
    }
  }, [isOpen, initialName, initialDefaultElaGradeLevel, isEditing]);

  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (defaultElaGradeLevel === "") {
      return; // Should not happen due to button disabled state
    }
    onSubmit({ name, defaultElaGradeLevel });
  };

  const title = isEditing ? `${ui.actions.edit} ${ui.labels.class}` : `${ui.actions.create} ${ui.labels.class}`;
  const submitLabel = isEditing ? ui.actions.save : ui.actions.create;
  const submitLoadingLabel = isEditing ? ui.actions.saving : ui.actions.creating;

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <ModalHeader title={title} onClose={onClose} />
      <ModalBody>
        <form onSubmit={handleSubmit} className="space-y-4" id="class-form" data-testid="class-form">
          <label className={fieldLabelClass}>
            {ui.labels.name}
            {isGCSynced ? (
              <Tooltip content={ui.googleClassroom.managedByGoogleClassroom} fullWidth>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className={disabledInputClass}
                  required
                  disabled
                  data-testid="class-name-input"
                />
              </Tooltip>
            ) : (
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className={inputClass}
                required
                autoFocus
                data-testid="class-name-input"
              />
            )}
          </label>
          <label className={fieldLabelClass}>
            {ui.labels.gradeLevel}
            <Select
              value={defaultElaGradeLevel}
              onChange={(e) => setDefaultElaGradeLevel(e.target.value as GradeLevel | "")}
              required
              data-testid="class-grade-select"
            >
              {isEditing && <option value="">{placeholders.classes.defaultGrade}</option>}
              {GRADE_LEVELS.map((grade) => (
                <option key={grade} value={grade}>
                  {ui.formatting.gradeSingle(parseInt(grade, 10))}
                </option>
              ))}
            </Select>
          </label>
        </form>
      </ModalBody>
      <ModalFooter>
        <button type="button" onClick={onClose} className={buttonVariants.secondary}>
          {ui.actions.cancel}
        </button>
        <button
          type="submit"
          form="class-form"
          className={buttonVariants.primary}
          disabled={isSubmitting || !name || !defaultElaGradeLevel}
          data-testid="class-form-submit"
        >
          {isSubmitting ? submitLoadingLabel : submitLabel}
        </button>
      </ModalFooter>
    </Modal>
  );
}
